import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, Shield, Truck, RefreshCw, Tag, ChevronRight, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCart } from '../context/CartContext';

const Cart = () => {
  const { cart, updateQuantity, removeFromCart, totalPrice, clearCart } = useCart();
  const navigate = useNavigate();

  const shipping = totalPrice >= 50 || totalPrice === 0 ? 0 : 5.99;
  const total = totalPrice + shipping;

  const handleCheckout = () => {
    if (cart.length === 0) return;
    navigate('/checkout');
  };

  if (cart.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <ShoppingBag size={80} className="mx-auto text-neutral-300 mb-4" />
        <h1 className="text-3xl font-black text-[#262626] mb-2">Votre panier est vide</h1>
        <p className="text-[#595959] mb-6">D\u00e9couvrez nos milliers de produits et commencez votre bricolage !</p>
        <Button onClick={() => navigate('/')} className="bg-[#00847A] hover:bg-[#006B63] text-white rounded-full h-12 px-8 font-semibold">
          Continuer mes achats
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <nav className="flex items-center gap-1 text-sm text-[#595959] mb-4">
        <Link to="/" className="hover:text-[#00847A]">Accueil</Link>
        <ChevronRight size={14} />
        <span className="text-[#262626] font-medium">Panier</span>
      </nav>

      <h1 className="text-3xl font-black text-[#262626] mb-6">Mon panier ({cart.length} article{cart.length > 1 ? 's' : ''})</h1>

      <div className="grid lg:grid-cols-[1fr_380px] gap-6">
        <div className="space-y-4">
          {cart.map(item => (
            <div key={item.id} className="bg-white border border-neutral-200 rounded-xl p-4 flex gap-4">
              <Link to={`/produit/${item.id}`} className="w-24 h-24 md:w-32 md:h-32 shrink-0 bg-neutral-50 rounded-lg overflow-hidden">
                <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
              </Link>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-[#595959] mb-1">{item.brand}</div>
                <Link to={`/produit/${item.id}`} className="font-semibold text-[#262626] hover:text-[#00847A] line-clamp-2">
                  {item.name}
                </Link>
                <div className="flex items-center gap-2 text-xs text-[#00847A] mt-1">
                  <Truck size={12} /> {item.shipping} \u2022 {item.delivery}
                </div>
                <div className="text-xs text-[#595959] mt-1">Vendu par {item.seller}</div>

                <div className="flex items-end justify-between mt-3">
                  <div className="flex items-center border border-neutral-300 rounded-full">
                    <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="w-8 h-8 flex items-center justify-center hover:bg-neutral-100 rounded-full"><Minus size={14} /></button>
                    <span className="w-8 text-center text-sm font-semibold">{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="w-8 h-8 flex items-center justify-center hover:bg-neutral-100 rounded-full"><Plus size={14} /></button>
                  </div>
                  <div className="text-right">
                    {item.oldPrice > item.price && (
                      <div className="text-xs text-[#595959] line-through">{(item.oldPrice * item.quantity).toFixed(2).replace('.', ',')} \u20ac</div>
                    )}
                    <div className="text-xl font-black text-[#262626]">{(item.price * item.quantity).toFixed(2).replace('.', ',')} \u20ac</div>
                  </div>
                </div>
              </div>
              <button onClick={() => removeFromCart(item.id)} className="shrink-0 text-[#595959] hover:text-[#FF4D4D] h-8 w-8 flex items-center justify-center">
                <Trash2 size={18} />
              </button>
            </div>
          ))}

          <div className="flex items-center justify-between pt-2">
            <Link to="/" className="text-sm text-[#00847A] font-semibold hover:underline">\u2190 Continuer mes achats</Link>
            <button onClick={clearCart} className="text-sm text-[#595959] hover:text-[#FF4D4D]">Vider le panier</button>
          </div>
        </div>

        {/* Summary */}
        <aside className="bg-white border border-neutral-200 rounded-xl p-5 h-fit sticky top-32">
          <h3 className="font-bold text-lg text-[#262626] mb-4">R\u00e9capitulatif</h3>

          <div className="space-y-2 text-sm text-[#262626]">
            <div className="flex justify-between">
              <span>Sous-total</span>
              <span className="font-semibold">{totalPrice.toFixed(2).replace('.', ',')} \u20ac</span>
            </div>
            <div className="flex justify-between">
              <span>Livraison</span>
              <span className="font-semibold">{shipping === 0 ? <span className="text-[#00847A]">Gratuite</span> : `${shipping.toFixed(2).replace('.', ',')} \u20ac`}</span>
            </div>
            {totalPrice < 50 && shipping > 0 && (
              <div className="text-xs text-[#FF4D4D] bg-rose-50 p-2 rounded">
                Plus que {(50 - totalPrice).toFixed(2).replace('.', ',')} \u20ac pour la livraison gratuite !
              </div>
            )}
          </div>

          <div className="border-t border-neutral-200 my-4 pt-4 flex justify-between items-baseline">
            <span className="font-bold text-[#262626]">Total TTC</span>
            <span className="text-2xl font-black text-[#262626]">{total.toFixed(2).replace('.', ',')} \u20ac</span>
          </div>

          <div className="flex gap-2 mb-4">
            <Input placeholder="Code promo" className="h-10 text-sm" />
            <Button variant="outline" className="h-10 border-[#00847A] text-[#00847A] hover:bg-[#00847A] hover:text-white"><Tag size={16} /></Button>
          </div>

          <Button onClick={handleCheckout} data-testid="checkout-btn" className="w-full bg-[#FF4D4D] hover:bg-[#E63939] text-white rounded-full h-12 font-semibold text-base disabled:opacity-70">
            <Lock size={16} className="mr-2" /> Passer la commande
          </Button>

          <div className="mt-5 space-y-2 text-xs text-[#595959]">
            <div className="flex items-center gap-2"><Shield size={14} className="text-[#00847A]" /> Paiement 100% s\u00e9curis\u00e9</div>
            <div className="flex items-center gap-2"><RefreshCw size={14} className="text-[#00847A]" /> Retours gratuits sous 30 jours</div>
            <div className="flex items-center gap-2"><Truck size={14} className="text-[#00847A]" /> Livraison gratuite d\u00e8s 50\u20ac</div>
          </div>

          <div className="mt-4 pt-4 border-t border-neutral-200">
            <div className="text-xs text-[#595959] mb-2">Nous acceptons</div>
            <div className="flex flex-wrap gap-1">
              {['VISA', 'MC', 'AMEX', 'PayPal', '3x'].map(p => (
                <div key={p} className="px-2 py-1 bg-neutral-100 text-[10px] font-bold text-[#262626] rounded">{p}</div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Cart;
